/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import {
    buildDeleteRequest,
    buildGetRequest,
    buildPostRequest,
    deleteHandler,
    getHandler,
    postHandler,
    stdErrHandlerFactory,
} from "../../utils/Requests";

import {
    Blank,
    Effect,
    Material,
    MaterialDraft,
    Nullable,
    Pagination,
    Reference,
    Setter,
    VersionedPagination,
} from "../../utils/Interfaces";

import { AnyAction, Dispatch } from "@reduxjs/toolkit";
import { AxiosResponse } from "axios";

export function getMaterialsDatabase() {
    return buildGetRequest<VersionedPagination<Material>>("/api/materials/list");
}

export function createMaterial(material: MaterialDraft) {
    return buildPostRequest<MaterialDraft, Reference>("/api/materials/create", material);
}

export function editMaterialDatabaseEntry(materialId: number, material: MaterialDraft) {
    return buildPostRequest<MaterialDraft, Reference>(`/api/materials/update/${materialId}`, material);
}

export function editMaterialDatabaseEntryByKey(
    materialId: number,
    updateKey: keyof MaterialDraft,
    newValue: Nullable<string | number>
) {
    return buildPostRequest<Partial<MaterialDraft>, Reference>(`/api/materials/update/${materialId}`, { [updateKey]: newValue });
}

export function deleteMaterial(materialId: number) {
    return buildDeleteRequest(`/api/materials/delete/${materialId}`);
}

export function filterMaterials(
    name: string,
    partNumber: string,
    dispatch: Dispatch<AnyAction>,
    setFilteredMaterials: Setter<Material[]>,
    onMaterialsFiltered: Effect,
    onMaterialsFilteredError: Effect,
    setLoading: Setter<boolean>
) {
    let queryString: string = "";

    if (name) {
        queryString += `?name=${name}`;
    }

    if (partNumber) {
        if (name) {
            queryString += `&part_number=${partNumber}`;
        } else {
            queryString += `?part_number=${partNumber}`;
        }
    }

    getHandler<Pagination<Material>>(
        `/api/materials/filter${queryString}`,
        dispatch,
        (response: AxiosResponse<Pagination<Material>, any>) => {
            setFilteredMaterials(response.data.results);
            onMaterialsFiltered();
        },
        stdErrHandlerFactory(dispatch, onMaterialsFilteredError),
        setLoading
    );
}

export function removeMaterialFromTrip(
    technicianEntryId: number,
    materialId: number,
    dispatch: Dispatch<AnyAction>,
    onMaterialRemoved: Effect,
    setLoading?: Setter<boolean>
) {
    deleteHandler<Blank>(
        `/api/materials/trips/remove/${technicianEntryId}/${materialId}`,
        dispatch,
        (response: AxiosResponse<Blank, any>) => onMaterialRemoved(),
        stdErrHandlerFactory(dispatch),
        setLoading
    );
}

export function addMaterialToTrip(
    technicianEntryId: number,
    materialId: number,
    amount: number,
    dispatch: Dispatch<AnyAction>,
    onMaterialAdded: Effect,
    setLoading?: Setter<boolean>
) {
    postHandler<Blank, Blank>(
        `/api/materials/trips/add/${technicianEntryId}/${materialId}/${amount}`,
        {},
        dispatch,
        (response: AxiosResponse<Blank, any>) => onMaterialAdded(),
        stdErrHandlerFactory(dispatch),
        setLoading
    );
}
